import { Skeleton } from "@/app/(site)/_components/Atoms/Skeleton"
import InformationButton from "@/app/(site)/_components/Page/Product/ProductDetail/InformationButton"
import TaxesInformationModal from "@/app/(site)/_components/Page/Product/ProductDetail/Modals/TaxesInformationModal"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { t } from "@/translations"
import { clsx } from "@/src/utils/clsx"

/**
 * Minimal totals shape PriceTotal reads. Both the Centra *selection* totals
 * (`CheckoutAPISelectionSelection["totals"]`) and the *receipt* totals structurally
 * satisfy this, so every caller passes its real Centra type with no cast.
 */
type PriceTotalsValues = {
    grandTotalPrice?: string
    grandTotalPriceTax?: string
    itemTotalPriceAfterCampaign?: string
    shippingPrice?: string
    shippingPriceAsNumber?: number
    taxAdded?: boolean
}

export function PriceTotal({
    langCode,
    totals,
    showShipping,
    filteredVouchers,
    totalDiscountVoucherDisplay,
    marketLayout,
    isLoadingSelection,
    showTaxIcon = true,
    hasFreeShipping
}: {
    langCode: string
    totals?: PriceTotalsValues
    showShipping: boolean
    filteredVouchers: {
        originalPriceOffAsNumber?: number
    }[]
    totalDiscountVoucherDisplay: string | boolean
    marketLayout: string
    isLoadingSelection: boolean
    showTaxIcon?: boolean
    hasFreeShipping: boolean
}) {
    const { checkoutCurrentStep, checkoutLoadedSteps } = useEcommerceStore()
    const { toggleTaxesInformationModal } = useEcommerceStore()
    const handleTaxesInformationClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        toggleTaxesInformationModal()
    }

    return isLoadingSelection ? (
        <div className="p-12 sm:p-16 md:p-24">
            <div className="flex flex-col gap-6 text-sm">
                <div className="flex items-end justify-between">
                    <Skeleton className="h-16 w-[55px]" />
                    <Skeleton className="h-16 w-[30px]" />
                </div>
                <div className="flex items-end justify-between">
                    <Skeleton className="h-16 w-[55px]" />
                    <Skeleton className="h-16 w-[30px]" />
                </div>
                <div className="flex items-end justify-between">
                    <Skeleton className="h-16 w-[75px]" />
                    <Skeleton className="h-16 w-[30px]" />
                </div>
                <div className="flex items-end justify-between">
                    <Skeleton className="h-24 w-[55px]" />
                    <Skeleton className="h-24 w-[40px]" />
                </div>
            </div>
        </div>
    ) : (
        <>
            <TaxesInformationModal langCode={langCode} />
            <div className="px-12 pt-24 sm:px-16 md:px-24 lg:pt-20">
                <div className="flex flex-col pb-16 text-sm">
                    <div className="flex items-end justify-between">
                        <span className="pt-3 Trade_Gothic_Text_Bold_14_150_0_700">
                            {t(langCode, "checkout.order_summary.subtotal")}
                        </span>
                        <span
                            className={clsx(
                                "py-2 Trim_Mono_POC_Text_14_130_-5_400_uppercase"
                            )}
                        >
                            {totals?.itemTotalPriceAfterCampaign || "0"}
                        </span>
                    </div>

                    {filteredVouchers?.length > 0 && (
                        <div className="flex items-end justify-between">
                            <span className="pt-3 Trade_Gothic_Text_Bold_14_150_0_700">
                                {t(langCode, "checkout.order_summary.voucher")}
                            </span>
                            <span
                                className={
                                    "py-2 Trim_Mono_POC_Text_14_130_-5_400_uppercase"
                                }
                            >
                                {totalDiscountVoucherDisplay &&
                                    totalDiscountVoucherDisplay}
                            </span>
                        </div>
                    )}

                    {showShipping && (
                        <div className="flex items-end justify-between">
                            <span className="pt-3 Trade_Gothic_Text_Bold_14_150_0_700">
                                {t(langCode, "checkout.order_summary.shipping_cost")}
                            </span>

                            {totals?.shippingPrice ? (
                                hasFreeShipping &&
                                (totals?.shippingPriceAsNumber ?? 0) > 0 &&
                                checkoutCurrentStep === 1 &&
                                !checkoutLoadedSteps.includes(2) ? (
                                    <span className="py-2 Trade_Gothic_Text_14_150_0_300">
                                        {t(
                                            langCode,
                                            "checkout.order_summary.to_be_calculated"
                                        )}
                                    </span>
                                ) : hasFreeShipping &&
                                  (totals?.shippingPriceAsNumber ?? 0) === 0 ? (
                                    <span className="py-2 Trim_Mono_POC_Text_14_130_-5_400_uppercase">
                                        {t(langCode, "checkout.shipping.free")}
                                    </span>
                                ) : (
                                    <span className="py-2 Trim_Mono_POC_Text_14_130_-5_400_uppercase">
                                        {totals?.shippingPrice}
                                    </span>
                                )
                            ) : (
                                <span className="py-2 Trade_Gothic_Text_14_150_0_300">
                                    {t(
                                        langCode,
                                        "checkout.order_summary.to_be_calculated"
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                    {marketLayout === "na" && totals?.grandTotalPriceTax && (
                        <div className="flex items-end justify-between">
                            <div
                                className="relative flex h-24 cursor-pointer items-center"
                                onClick={e =>
                                    showTaxIcon
                                        ? handleTaxesInformationClick(e)
                                        : undefined
                                }
                            >
                                <span className="mr-4 pt-2 Trade_Gothic_Text_Bold_14_150_0_700">
                                    {t(
                                        langCode,
                                        "checkout.order_summary.estimated_taxes"
                                    )}
                                </span>
                                {showTaxIcon && (
                                    <InformationButton
                                        onClick={handleTaxesInformationClick}
                                        ariaLabel={"title"}
                                        className="h-12 w-12"
                                    />
                                )}
                            </div>
                            &nbsp;
                            {totals?.taxAdded ? (
                                <span className="py-2 Trim_Mono_POC_Text_14_130_-5_400_uppercase">
                                    {totals?.grandTotalPriceTax}
                                </span>
                            ) : (
                                <span className="py-2 Trade_Gothic_Text_14_150_0_300">
                                    {t(
                                        langCode,
                                        "checkout.order_summary.to_be_calculated"
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t border-col-border-subtle py-16">
                    <div className="flex items-end justify-between">
                        <span className="Trade_Gothic_Text_Bold_14_150_0_700 lg:Trade_Gothic_Text_Bold_16_150_0_700">
                            {t(langCode, "checkout.order_summary.total")}
                        </span>
                        <span className="Trim_Mono_POC_Text_15_130_-5_500_uppercase lg:Trim_Mono_POC_Text_16_130_-5_500_uppercase">
                            {totals?.grandTotalPrice}
                        </span>
                    </div>
                    {marketLayout !== "na" && totals?.grandTotalPriceTax && (
                        <div className="-mt-4 text-right">
                            <span className="Trade_Gothic_Text_12_150_0_300">
                                {t(langCode, "checkout.order_summary.included_taxes")}
                            </span>
                            &nbsp;
                            <span className="Trim_Mono_POC_Text_12_130_-3_300_uppercase">
                                {totals?.grandTotalPriceTax}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
